/**
 * Value Validators
 *
 * Schema-based validators for each CSS token value type. Used to check
 * that a token's value matches its expected format (hex color, rem length, etc).
 *
 * @module
 */
import { Schema as S } from "effect"
import type { TokenType } from "./types"

// ── css-color ────────────────────────────────────────────────────────────────
// Hex: #rgb, #rgba, #rrggbb, #rrggbbaa
const HexColor = S.String.check(
  S.isPattern(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/)
)

// rgb() / rgba() — modern and legacy syntax
const RgbColor = S.String.check(
  S.isPattern(/^rgba?\(\s*[\d.]+(%?\s*[,/\s]\s*[\d.]+%?){2,3}\s*\)$/)
)

// hsl() / hsla()
const HslColor = S.String.check(
  S.isPattern(/^hsla?\(\s*[\d.]+\s*(deg|rad|grad|turn)?\s*[,\s]\s*[\d.]+%\s*[,\s]\s*[\d.]+%\s*([,/]\s*[\d.]+%?)?\s*\)$/)
)

const CssColor = S.Union([HexColor, RgbColor, HslColor])

// ── css-length ───────────────────────────────────────────────────────────────
// Bare 0 or number + unit (rem, px, em, %, vh, vw, ch)
const CssLength = S.String.check(
  S.isPattern(/^0$|^-?[\d.]+(rem|px|em|%|vh|vw|ch)$/)
)

// ── font-stack ───────────────────────────────────────────────────────────────
// Comma-separated list with at least one named font + a generic family
const GENERIC_FAMILIES = new Set([
  "serif", "sans-serif", "monospace", "cursive", "fantasy",
  "system-ui", "ui-serif", "ui-sans-serif", "ui-monospace", "ui-rounded",
  "math", "emoji", "fangsong",
])

const FontStack = S.String.check(
  S.makeFilter<string>(
    (value) => {
      const fonts = value.split(",").map((f) => f.trim())
      if (fonts.length < 2) return false
      const last = fonts[fonts.length - 1].toLowerCase()
      return GENERIC_FAMILIES.has(last)
    },
  )
)

// ── font-provider-var ────────────────────────────────────────────────────────
// Must be var(--font-provider-heading) or var(--font-provider-body)
const FontProviderVar = S.String.check(
  S.isPattern(/^var\(--font-provider-(heading|body)\)$/)
)

// ── css-shadow ───────────────────────────────────────────────────────────────
// Must contain numeric offsets and a color function
const CssShadow = S.String.check(
  S.makeFilter<string>(
    (value) => {
      const hasNumbers = /\d/.test(value)
      const hasColor = /rgb|rgba|hsl|hsla|#[0-9a-fA-F]/.test(value)
      return hasNumbers && hasColor
    },
  )
)

// ── Validator + Message per type ──────────────────────────────────────────────

const getValidator = (type: TokenType) => {
  switch (type) {
    case "css-color":  return CssColor
    case "css-length": return CssLength
    case "font-stack": return FontStack
    case "font-provider-var": return FontProviderVar
    case "css-shadow": return CssShadow
  }
}

const EXPECTED_MESSAGES: Record<TokenType, string> = {
  "css-color": "Expected a CSS color (hex like #fff or #ffffff, rgb(), or hsl())",
  "css-length": "Expected a CSS length (e.g. 1rem, 16px, 0)",
  "font-stack": "Expected a font stack ending with a generic family (e.g. \"Inter\", system-ui, sans-serif)",
  "font-provider-var": "Expected a font provider variable: var(--font-provider-heading) or var(--font-provider-body). Fonts are managed via Astro's Google Font Provider in astro.config.mjs",
  "css-shadow": "Expected a CSS shadow value with offsets and color (e.g. 0 1px 3px rgb(0 0 0 / 0.08))",
}

/**
 * Validate a token value against its expected type.
 * Returns null if valid, or an error message string if invalid.
 */
export const validateTokenValue = (value: string, type: TokenType): string | null => {
  try {
    S.decodeUnknownSync(getValidator(type))(value)
    return null
  } catch {
    return EXPECTED_MESSAGES[type]
  }
}
